import { useState, useRef, useEffect, memo } from "react";
import { createPortal } from "react-dom";
import { ThumbsUp, ThumbsDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils";

interface AiAgentFeedbackProps {
	messageId: string;
	currentFeedback?: "positive" | "negative";
	onSubmitFeedback: (
		messageId: string,
		rating: "positive" | "negative",
		reason?: string,
		comment?: string,
	) => void;
}

const REASON_KEYS = [
	{ key: "incorrect", i18n: "agent.feedback_reason_incorrect" },
	{ key: "incomplete", i18n: "agent.feedback_reason_incomplete" },
	{ key: "not_helpful", i18n: "agent.feedback_reason_not_helpful" },
	{ key: "unclear", i18n: "agent.feedback_reason_unclear" },
	{ key: "other", i18n: "agent.feedback_reason_other" },
] as const;

const AiAgentFeedbackComponent = ({
	messageId,
	currentFeedback,
	onSubmitFeedback,
}: AiAgentFeedbackProps) => {
	const { t } = useTranslation();
	const [showReasons, setShowReasons] = useState(false);
	const [selectedReason, setSelectedReason] = useState<string | null>(null);
	const [comment, setComment] = useState("");
	const [messagesContainer, setMessagesContainer] = useState<Element | null>(
		null,
	);
	const selfRef = useRef<HTMLDivElement>(null);

	const isSubmitted = !!currentFeedback;

	useEffect(() => {
		if (selfRef.current) {
			setMessagesContainer(selfRef.current.closest(".ai-chat-panel"));
		}
	}, []);

	const handleThumbsUp = () => {
		if (isSubmitted) return;
		onSubmitFeedback(messageId, "positive");
	};

	const handleThumbsDown = () => {
		if (isSubmitted) return;
		setShowReasons(true);
	};

	const handleCancel = () => {
		setShowReasons(false);
		setSelectedReason(null);
		setComment("");
	};

	const handleSubmitNegative = () => {
		onSubmitFeedback(
			messageId,
			"negative",
			selectedReason ?? undefined,
			comment || undefined,
		);
		setShowReasons(false);
		setSelectedReason(null);
		setComment("");
	};

	return (
		<div className="ai-chat-feedback" ref={selfRef}>
			<div className="ai-chat-feedback-buttons">
				<button
					type="button"
					className={cn(
						"ai-chat-feedback-btn",
						currentFeedback === "positive"
							? "ai-chat-feedback-btn--active"
							: "",
					)}
					onClick={handleThumbsUp}
					disabled={isSubmitted}
					title={t("agent.feedback_positive")}
					aria-label={t("agent.feedback_positive")}
				>
					<ThumbsUp className="ai-chat-feedback-icon" />
				</button>
				<button
					type="button"
					className={cn(
						"ai-chat-feedback-btn",
						currentFeedback === "negative"
							? "ai-chat-feedback-btn--active"
							: "",
					)}
					onClick={handleThumbsDown}
					disabled={isSubmitted}
					title={t("agent.feedback_negative")}
					aria-label={t("agent.feedback_negative")}
				>
					<ThumbsDown className="ai-chat-feedback-icon" />
				</button>
			</div>

			{showReasons &&
				!isSubmitted &&
				messagesContainer &&
				createPortal(
					<div className="ai-chat-feedback-overlay">
						<div
							className="ai-chat-feedback-reasons"
							onKeyDown={(e) => {
								if (e.key === "Escape") handleCancel();
							}}
						>
							<button
								type="button"
								className="ai-chat-feedback-reasons-close"
								onClick={handleCancel}
								aria-label={t("agent.close")}
							>
								<X className="w-3.5 h-3.5" />
							</button>
							<p className="ai-chat-feedback-reasons-title">
								{t("agent.feedback_reason_title")}
							</p>
							<div className="ai-chat-feedback-chips">
								{REASON_KEYS.map(({ key, i18n }) => (
									<button
										key={key}
										type="button"
										className={cn(
											"ai-chat-feedback-chip",
											selectedReason === key
												? "ai-chat-feedback-chip--active"
												: "",
										)}
										onClick={() =>
											setSelectedReason(selectedReason === key ? null : key)
										}
									>
										{t(i18n)}
									</button>
								))}
							</div>
							<textarea
								className="ai-chat-feedback-textarea"
								placeholder={t("agent.feedback_comment_placeholder")}
								value={comment}
								onChange={(e) => setComment(e.target.value.slice(0, 500))}
								rows={2}
								maxLength={500}
							/>
							<div
								className="ai-chat-feedback-actions"
								onKeyDown={(e) => e.stopPropagation()}
							>
								<button
									type="button"
									className="ai-chat-feedback-submit"
									onClick={handleSubmitNegative}
								>
									{t("agent.feedback_submit")}
								</button>
							</div>
						</div>
					</div>,
					messagesContainer,
				)}
		</div>
	);
};

export const AiAgentFeedback = memo(AiAgentFeedbackComponent);
